import { useState, useEffect, useCallback, useRef } from 'react';
import { callAPI } from '../utils/apiUtils';
import { apiUrls } from '../utils/apiUrls';
import { getSessionId } from '../utils/sessionId';
import { HTTP_STATUS } from '../utils/httpStatusCodes';
import { toast } from 'react-toastify';
import { buildItems, buildCartTotals, makePayload } from '../utils/customerDisplay';
import { sendCustomerDisplay, isCustomerDisplayAvailable } from '../utils/customerDisplayBridge';



// Instant cart edits: a quantity change or a removal is shown at once and the server answer is
// reconciled afterwards (a failed call just reloads what the server has). Kill switch for a single
// device: localStorage.cart_optimistic = '0'.
const optimisticCartEnabled = () => {
  try {
    return window.localStorage.getItem('cart_optimistic') !== '0';
  } catch {
    return true;
  }
};

// The unit price of a cart row, from its own line total (covers extras and size prices already in it).
const unitPriceOf = (row) => {
  const qty = Number(row?.item_quantity);
  const total = Number(row?.total_price);
  return qty > 0 && Number.isFinite(total) ? total / qty : null;
};

const money = (n) => Math.round(Number(n) * 100) / 100;
const isPendingId = (id) => typeof id === 'string' && id.startsWith('pending-');

const useCart = (refresh, editOrderId = null, { skipInitialFetch = false, mergePending = false } = {}) => {
  const [cartItems, setCartItems] = useState([]);
  const [netTotal, setNetTotal] = useState(0);
  const [dealsItem, setDealsItem] = useState([]);
  const [halfAndHalfItems, setHalfAndHalfItems] = useState([]);
  const [removeID, setRemoveID] = useState(null);
  const [servers, setServers] = useState([]); 
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipText, setTooltipText] = useState('');
  // Customer screen: only start describing the cart once the first fetch has answered, so a page
  // that is still loading does not flash the idle logo.
  const [cartLoaded, setCartLoaded] = useState(false);
  // Cart edits (quantity, removal) whose server call has not answered yet. The cart is reloaded from
  // the server only once the last one has, so a late answer to an earlier tap cannot put an old
  // quantity back on screen while later taps are still on their way.
  const pendingEditsRef = useRef(0);
  // The rows as last rendered, for working out an instant edit without side effects in a state updater.
  const cartItemsRef = useRef([]);
  cartItemsRef.current = cartItems;
  // Items being added: a row is shown the moment the item is tapped, before the server has answered.
  // Each entry is { row, settledAfter }: `settledAfter` stays null while the add is on its way, and
  // is the number of the last cart load that had STARTED when the server confirmed it. A cart load
  // that started after that has the real row in it, so it replaces the placeholder; one that started
  // earlier may not have it yet, and must not make the placeholder disappear.
  const pendingAddsRef = useRef([]);
  const pendingIdRef = useRef(0);
  const fetchSeqRef = useRef(0);
  const [hasPendingAdds, setHasPendingAdds] = useState(false);

  
  const fetchCartWithDeals = async () => {
    const mySeq = ++fetchSeqRef.current;
    const session_id = getSessionId();
    let url = apiUrls.GET_ADDED_TO_CART + `?session_id=${session_id}`;
    if (editOrderId) {
      url += `&edit_order_id=${editOrderId}`; // pass edit_order_id
    }
    // Only set when editOrderId was resolved by table lookup rather than passed explicitly (see
    // Takeaway.js) - tells the backend it's safe to merge this session's still-pending items into
    // an order's cart. Sending it for an explicit edit_order_id (e.g. "Add More Items" on a
    // PREPAID order) would pull in unrelated pending items from any other order open in this same
    // browser session.
    if (mergePending) {
      url += `&merge_pending=1`;
    }
    const response = await callAPI(
      url,
      {},
      'GET',
      null, {}, {}, false
    );
    if (response?.data?.data) {
      // placeholders still waiting for their add (or confirmed by a newer answer than this one) stay on screen
      pendingAddsRef.current = pendingAddsRef.current.filter((p) => p.settledAfter === null || mySeq <= p.settledAfter);
      const kept = pendingAddsRef.current;
      const keptRows = (kind) => kept.filter((p) => p.kind === kind).map((p) => p.row);
      setCartItems([...(response.data.data.items || []), ...keptRows('item')]);
      setDealsItem([...(response.data.data.data_deals || []), ...keptRows('deal')]);
      setHalfAndHalfItems([...(response.data.data.data_half_and_half || []), ...keptRows('half')]);
      setNetTotal(money((Number(response.data.data.net_total) || 0) + kept.reduce((t, p) => t + Number(p.amount ?? p.row.total_price ?? 0), 0)));
      setHasPendingAdds(kept.length > 0);
      setServers(response.data.servers || []); 
      setCartLoaded(true);
      console.log("CART ITEMS", response.data.data.items);
      console.log("DEAL ITEMS", response.data.data.data_deals);
    }
  };

  
  
  const fetchCartDetails = async () => {
    const session_id = getSessionId();
    let url = apiUrls.CART_ITEM_DETAILS + `?session_id=${session_id}`;
    if (editOrderId) {
      url += `&edit_order_id=${editOrderId}`; 
    }
    const response = await callAPI(
      url,
      {},
      'GET'
    );
    if (response?.data?.data) {
      setCartItems(response.data.data.items || []);
      setNetTotal(response.data.data.net_total || 0);
    }
  };

  
  const handleUpdateQty = async (cart_id, newQty) => {
    // A quantity of zero (or less) is a removal: same path, same busy state and toasts as the Remove
    // button, which is what older servers need (update-quantity with 0 only removes on newer ones).
    if (isPendingId(cart_id)) return undefined; // still being added: there is no server row to change yet
    if (!(Number(newQty) > 0)) return handleRemoveItem(cart_id);
    pendingEditsRef.current += 1;
    // When the change is already on screen the call runs quietly: the full-screen loader would sit over the
    // cart for the whole round trip and undo the point of showing it at once.
    let shownAtOnce = false;
    try {
      if (optimisticCartEnabled()) {
        const qty = Number(newQty);
        const row = cartItemsRef.current.find((r) => r.cart_id === cart_id);
        const unit = unitPriceOf(row);
        if (row && unit != null) { // not a plain row (or no price to scale): wait for the server instead
          const total = Math.round(unit * qty * 100) / 100;
          const delta = total - Number(row.total_price);
          setCartItems((rows) => rows.map((r) => (r.cart_id === cart_id ? { ...r, item_quantity: qty, total_price: total } : r)));
          setNetTotal((t) => Math.round((Number(t) + delta) * 100) / 100);
          shownAtOnce = true;
        }
      }
      //const session_id = getSessionId();
      const response = await callAPI(
        apiUrls.UPDATE_QUANTITY,
        {},
        'POST',
        { id: cart_id, quantity: newQty },
        {},
        {},
        !shownAtOnce
      );

      if (response && response.status === HTTP_STATUS.OK) {
        toast.success('Quantity updated');
      } else {
        console.error('Error updating quantity:', response);
        toast.error('Failed to update quantity');
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
      toast.error('Failed to update quantity');
    } finally {
      pendingEditsRef.current -= 1;
    }
    // success or failure the list ends up showing what the server has - once, after the last edit
    if (pendingEditsRef.current === 0) {
      try {
        await fetchCartWithDeals();
      } catch (error) {
        console.error('Error refreshing the cart after a quantity change:', error);
      }
    }
  };

  
  // Rows with a remove call in flight: a second tap on the same row is ignored until it finishes.
  const removingRef = useRef(new Set());

  const handleRemoveItem = async (cart_id) => {
    if (isPendingId(cart_id)) return false; // still being added: there is no server row to remove yet
    if (removingRef.current.has(cart_id)) return false;
    removingRef.current.add(cart_id);
    let removed = false;
    let shownAtOnce = false;
    pendingEditsRef.current += 1;
    try {
      setRemoveID(cart_id);
      if (optimisticCartEnabled()) {
        // the row disappears at once; a failed call reloads the cart below, which brings it back
        const row = cartItemsRef.current.find((r) => r.cart_id === cart_id);
        if (row) {
          setCartItems((rows) => rows.filter((r) => r.cart_id !== cart_id));
          setNetTotal((t) => Math.max(0, Math.round((Number(t) - Number(row.total_price || 0)) * 100) / 100));
          shownAtOnce = true;
        }
      }
      const session_id = getSessionId();
      const payload = { id: cart_id, session_id };
      if (editOrderId) {
        payload.edit_order_id = editOrderId;
      }
      const response = await callAPI(apiUrls.REMOVE_CART_ITEM, {}, 'POST', payload, {}, {}, !shownAtOnce);
      if (response && response.status === HTTP_STATUS.OK) {
        toast.success('Item removed');
        removed = true;
      } else {
        console.error('Error removing item:', response);
        toast.error('Failed to remove item');
      }
    } catch (error) {
      console.error('Error removing item:', error);
      toast.error(error?.response?.status === 404 ? 'That item is no longer in the cart' : 'Failed to remove item');
    } finally {
      // success or failure: the row is usable again, and the list shows what the server has
      removingRef.current.delete(cart_id);
      setRemoveID(null);
      pendingEditsRef.current -= 1;
    }
    if (pendingEditsRef.current === 0) {
      try {
        await fetchCartWithDeals();
      } catch (error) {
        console.error('Error refreshing the cart after a remove:', error);
      }
    }
    return removed;
  };

  
  // ---- instant add ----
  // Shows `spec` as a cart row at once. Returns an id to settle or fail it with, or null when the
  // device has the instant cart switched off (the caller then just waits for the server as before).
  const addPendingRow = (spec) => {
    if (!optimisticCartEnabled()) return null;
    const qty = Number(spec.qty) > 0 ? Number(spec.qty) : 1;
    const total = money(spec.total_price != null ? spec.total_price : Number(spec.unit_price) * qty);
    if (!Number.isFinite(total) || total < 0) return null; // no price to show: wait for the server
    const tempId = `pending-${++pendingIdRef.current}`;
    const row = {
      first_image_url: spec.first_image_url || '',
      item_name: spec.item_name,
      selected_size: spec.selected_size || '',
      cart_id: tempId,
      item_id: spec.item_id,
      item_quantity: qty,
      per_item_price: money(total / qty),
      total_price: total,
      is_previously_added: false,
      product_detail_text: '',
      comments: '',
      pending: true,
    };
    pendingAddsRef.current = [...pendingAddsRef.current, { row, kind: 'item', amount: total, settledAfter: null }];
    setCartItems((rows) => [...rows, row]);
    setNetTotal((t) => money(Number(t) + total));
    setHasPendingAdds(true);
    return tempId;
  };

  // A Half n Half: the same, but it is a parent line with its two halves under it, in the half-and-half list.
  // spec: { total_price, crust_label, crust_price, halves: [{ name, size, image }, { name, size, image }] }
  const addPendingHalf = (spec) => {
    if (!optimisticCartEnabled()) return null;
    const total = money(spec.total_price);
    if (!Number.isFinite(total) || !(total > 0)) return null; // no price to show: wait for the server
    const tempId = `pending-${++pendingIdRef.current}`;
    const halves = (spec.halves || []).map((h, i) => ({
      first_image_url: h.image || '',
      product_thumbnail: h.image || '',
      item_name: h.name,
      selected_size: h.size || '',
      cart_id: `${tempId}-half${i + 1}`,
      item_quantity: 1,
      per_item_price: 0,
      total_price: 0,
      product_detail_text: '',
      toppings: [],
      comments: '',
      pending: true,
    }));
    const row = {
      item_name: 'Half n Half',
      cart_id: tempId,
      cid: tempId,
      item_quantity: 1,
      per_item_price: total,
      total_price: total,
      is_previously_added: false,
      crust_label: spec.crust_label || '',
      crust_price: spec.crust_price ?? 0,
      [tempId]: halves,
      pending: true,
    };
    pendingAddsRef.current = [...pendingAddsRef.current, { row, kind: 'half', amount: total, settledAfter: null }];
    setHalfAndHalfItems((rows) => [...rows, row]);
    setNetTotal((t) => money(Number(t) + total));
    setHasPendingAdds(true);
    return tempId;
  };

  // A Deal: a parent line with the chosen items under it, in the deals list.
  // spec: { item_name, total_price, children: [{ name, size, image, upgrade }] }
  const addPendingDeal = (spec) => {
    if (!optimisticCartEnabled()) return null;
    const total = money(spec.total_price);
    if (!Number.isFinite(total) || !(total > 0)) return null; // no price to show: wait for the server
    // The deal's own line shows its base price and each item its upgrade, the way the server draws it; the cart total is the sum.
    const basePrice = spec.base_price != null ? money(spec.base_price) : total;
    const tempId = `pending-${++pendingIdRef.current}`;
    const children = (spec.children || []).map((c, i) => ({
      first_image_url: c.image || '',
      product_thumbnail: c.image || '',
      item_name: c.name,
      selected_size: c.size || '',
      cart_id: `${tempId}-item${i + 1}`,
      item_quantity: 1,
      per_item_price: money(c.upgrade || 0),
      total_price: money(c.upgrade || 0),
      comments: '',
      pending: true,
    }));
    const row = {
      item_name: spec.item_name,
      cart_id: tempId,
      cid: tempId,
      item_quantity: 1,
      per_item_price: basePrice,
      total_price: basePrice,
      is_previously_added: false,
      productDetails: '',
      highlighter: '',
      [tempId]: children,
      pending: true,
    };
    pendingAddsRef.current = [...pendingAddsRef.current, { row, kind: 'deal', amount: total, settledAfter: null }];
    setDealsItem((rows) => [...rows, row]);
    setNetTotal((t) => money(Number(t) + total));
    setHasPendingAdds(true);
    return tempId;
  };

  // The server has the item: the row stays until the next cart load that started after this point
  // brings the real one (the caller asks for that load).
  const settlePendingRow = (tempId) => {
    pendingAddsRef.current = pendingAddsRef.current.map((p) => (p.row.cart_id === tempId ? { ...p, settledAfter: fetchSeqRef.current } : p));
  };

  // The server refused it (or could not be reached): take the row back out.
  const failPendingRow = (tempId) => {
    const found = pendingAddsRef.current.find((p) => p.row.cart_id === tempId);
    if (!found) return;
    pendingAddsRef.current = pendingAddsRef.current.filter((p) => p.row.cart_id !== tempId);
    if (found.kind === 'half') setHalfAndHalfItems((rows) => rows.filter((r) => r.cart_id !== tempId));
    else if (found.kind === 'deal') setDealsItem((rows) => rows.filter((r) => r.cart_id !== tempId));
    else setCartItems((rows) => rows.filter((r) => r.cart_id !== tempId));
    setNetTotal((t) => Math.max(0, money(Number(t) - Number(found.amount ?? found.row.total_price ?? 0))));
    setHasPendingAdds(pendingAddsRef.current.length > 0);
  };

  useEffect(() => {
    if (skipInitialFetch) return;
    const fetchData = async () => {
      await fetchCartWithDeals();
      //await fetchCartDetails();
    };
    fetchData();
  }, [refresh, editOrderId]);


// useEffect(() => {

//   if (!window.AndroidPOS?.updateCart) return;

//   const timeout = setTimeout(() => {

//     const payload = {
//       items: [
//         ...cartItems,
//         ...dealsItem
//       ],
//       total: netTotal
//     };

//     window.AndroidPOS.updateCart(JSON.stringify(payload));

//   }, 150); // debounce kar lete hai

//   return () => clearTimeout(timeout);

// }, [cartItems, dealsItem, netTotal]);




useEffect(() => {

  if (!window.AndroidPOS?.updateCart) return;

  const timeout = setTimeout(() => {

    const androidItems = [
      ...cartItems,
      ...dealsItem.map(deal => ({
        cart_id: deal.cart_id,
        item_id: deal.product_id || deal.item_id,
        item_name: deal.item_name,
        item_quantity: deal.item_quantity || 1,
        first_image_url: deal.product_thumbnail,
        total_price: deal.totalwithdeals ?? deal.total_price,
        per_item_price: deal.totalwithdeals ?? deal.total_price,
        selected_size: "",
        product_detail_text: ""
      }))
    ];

    window.AndroidPOS.updateCart(JSON.stringify({
      items: androidItems,
      total: netTotal
    }));

  },150);

  return () => clearTimeout(timeout);

}, [cartItems, dealsItem, netTotal]);



// useEffect(() => {

//   if (!window.AndroidPOS?.updateCart) return;

//   const timeout = setTimeout(() => {

//     const androidItems = [
//       ...cartItems,

//       ...dealsItem.flatMap(deal => {

//         const dealChildren = deal[deal.cart_id] || [];

//         return [
//           // Parent deal
//           {
//             cart_id: deal.cart_id,
//             item_id: deal.product_id || deal.item_id,
//             item_name: deal.item_name,
//             item_quantity: deal.item_quantity || 1,
//             first_image_url: deal.product_thumbnail,
//             total_price: deal.totalwithdeals ?? deal.total_price,
//             per_item_price: deal.totalwithdeals ?? deal.total_price,
//             selected_size: "",
//             product_detail_text: ""
//           },

//           // Deal child items
//           ...dealChildren.map(child => ({
//             cart_id: child.cart_id,
//             item_id: child.item_id,
//             item_name: "  - " + child.item_name,
//             item_quantity: child.item_quantity || 1,
//             first_image_url: child.first_image_url,
//             total_price: child.total_price,
//             per_item_price: child.per_item_price,
//             selected_size: child.selected_size || "",
//             product_detail_text: child.product_detail_text || ""
//           }))
//         ];

//       })
//     ];


//     console.log("ANDROID CART PAYLOAD", androidItems);


//     window.AndroidPOS.updateCart(JSON.stringify({
//       items: androidItems,
//       total: netTotal
//     }));


//   }, 150);


//   return () => clearTimeout(timeout);


// }, [cartItems, dealsItem, netTotal]);


useEffect(() => {
  const checkBridge = setInterval(() => {
    if (window.AndroidPOS?.updateCart) {
      clearInterval(checkBridge);
    }
  }, 500);

  return () => clearInterval(checkBridge);
}, []);

// Customer (second) screen: 'cart' with the items, their customisations and the running total once
// the cart has anything in it, 'idle' when it is empty. Only instances that fetch the cart describe
// it - the reset-only instances (skipInitialFetch) have no cart state and just emit idle from
// resetCart(). A no-op without a wrapper that has updateCustomerDisplay.
useEffect(() => {
  if (skipInitialFetch || !cartLoaded || !isCustomerDisplayAvailable()) return;

  const timeout = setTimeout(() => {
    const items = buildItems(cartItems, dealsItem, halfAndHalfItems);
    sendCustomerDisplay(
      items.length
        ? makePayload('cart', { items, totals: buildCartTotals(netTotal) })
        : makePayload('idle')
    );
  }, 150);

  return () => clearTimeout(timeout);
}, [skipInitialFetch, cartLoaded, cartItems, dealsItem, halfAndHalfItems, netTotal]);

// useEffect(() => {
//   if (!window.AndroidPOS?.updateCart) return;

//   window.AndroidPOS.updateCart(JSON.stringify({
//     items: [
//       ...cartItems,
//       ...dealsItem
//     ],
//     total: netTotal
//   }));

// }, []);

const resetCart = useCallback(() => {
  setCartItems([]);
  setDealsItem([]);
  setHalfAndHalfItems([]);
  setNetTotal(0);

  if (window.AndroidPOS?.updateCart) {
    window.AndroidPOS.updateCart(JSON.stringify({
      items: [],
      total: 0
    }));
  }

  sendCustomerDisplay(makePayload('idle'));
}, []);

  return {
    cartItems,
    netTotal,
    dealsItem,
    halfAndHalfItems,
    removeID,
    servers, 
    tooltipVisible,
    tooltipText,
    setTooltipVisible,
    setTooltipText,
    handleUpdateQty,
    handleRemoveItem,
    fetchCartWithDeals,
    fetchCartDetails,
    resetCart,
    hasPendingAdds,
    addPendingRow,
    addPendingHalf,
    addPendingDeal,
    settlePendingRow,
    failPendingRow
  };
};

export default useCart;
